import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { fetchSection, mapShopifyProduct } from "@/lib/shopify";

const SECTION_META = {
  wear: {
    title: "Wear",
    subtitle: "Clothing that fits your body and your life. Every piece is tried on, tested, and chosen by people who get it.",
  },
  "big-living": {
    title: "Big Living",
    subtitle: "Your home, your adventures, your life — without the worry. We checked.",
  },
};

export default function ShopAllPage() {
  const { section } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const meta = SECTION_META[section] || { title: section, subtitle: "" };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const result = await fetchSection(section, 250);
      if (!cancelled) {
        setProducts((result.products || []).map(mapShopifyProduct));
        setTotal(result.total || result.products?.length || 0);
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [section]);

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="shop-all-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Back */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-plum-light hover:text-plum mb-8 transition-colors duration-200"
          data-testid="shop-all-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10 lg:mb-14"
        >
          <h1 className="font-serif text-4xl lg:text-5xl font-bold text-plum tracking-tight">
            {meta.title}
          </h1>
          <p className="mt-3 text-base text-plum-light max-w-xl leading-relaxed">
            {meta.subtitle}
          </p>
          {!loading && (
            <p className="mt-2 text-sm text-plum-muted">
              {total} product{total !== 1 ? "s" : ""}
            </p>
          )}
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-plum/20 border-t-plum rounded-full animate-spin" />
          </div>
        )}

        {/* Product grid */}
        {!loading && (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-8"
            data-testid="shop-all-grid"
          >
            {products.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>
        )}

        {!loading && products.length === 0 && (
          <div className="text-center py-20">
            <p className="text-plum-light">No products found in this section.</p>
          </div>
        )}
      </div>
    </div>
  );
}
